import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Plus, Search, Edit, Trash2, Users as UsersIcon, Key, Shield } from 'lucide-react'
import { toast } from 'react-toastify'
import { usersAPI, adminAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'
import UserModal from '../components/UserModal'
import AssignResourcesModal from '../components/AssignResourcesModal'
import ConfirmDialog from '../components/ConfirmDialog'

function Users() {
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [selectedUser, setSelectedUser] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [assignUser, setAssignUser] = useState(null)
  const [deleteUser, setDeleteUser] = useState(null)
  const [bulkSelection, setBulkSelection] = useState([])
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const queryParams = {
    search: searchTerm,
    role: roleFilter
  }

  const { data: users, isLoading } = useQuery(
    ['users', queryParams],
    () => usersAPI.getUsers(queryParams)
  )

  const deleteMutation = useMutation(usersAPI.deleteUser, {
    onSuccess: () => {
      toast.success('User deleted successfully')
      queryClient.invalidateQueries('users')
      setDeleteUser(null)
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Delete failed')
    }
  })

  const bulkActionMutation = useMutation(adminAPI.bulkUserAction, {
    onSuccess: (data) => {
      toast.success(data.data.message)
      queryClient.invalidateQueries('users')
      setBulkSelection([])
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Bulk action failed')
    }
  })

  const filteredUsers = users?.data?.users || []

  const handleEdit = (userData) => {
    setSelectedUser(userData)
    setIsModalOpen(true)
  }

  const handleCreate = () => {
    setSelectedUser(null)
    setIsModalOpen(true)
  }

  const handleDelete = (userData) => {
    setDeleteUser(userData)
  }

  const handleAssignResources = (userData) => {
    setAssignUser(userData)
  }

  const confirmDelete = () => {
    if (deleteUser) {
      deleteMutation.mutate(deleteUser._id)
    }
  }

  const handleBulkAction = (action) => {
    if (bulkSelection.length === 0) {
      toast.warning('Please select users first')
      return
    }
    
    bulkActionMutation.mutate({
      action,
      userIds: bulkSelection
    })
  }

  const handleSelectAll = () => {
    if (bulkSelection.length === filteredUsers.length) {
      setBulkSelection([])
    } else {
      setBulkSelection(filteredUsers.map(u => u._id))
    }
  }

  const handleSelectUser = (userId) => {
    setBulkSelection(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <Shield className="mx-auto h-12 w-12 text-primary-400" />
        <h3 className="mt-2 text-sm font-medium text-primary-900">Access Denied</h3>
        <p className="mt-1 text-sm text-primary-500">
          You need admin privileges to manage users.
        </p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-900">Users</h1>
          <p className="mt-1 text-sm text-primary-600">
            Manage user accounts and permissions
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={handleCreate}
            className="btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            New User
          </button>
        </div>
      </div>

      {/* Filters and Bulk Actions */}
      <div className="card">
        <div className="card-body">
          <div className="sm:flex sm:items-center sm:justify-between">
            <div className="flex flex-1 space-x-4">
              <div className="max-w-md flex-1">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-primary-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-field pl-10"
                  />
                </div>
              </div>
              <div className="min-w-0">
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="input-field"
                >
                  <option value="">All roles</option>
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                </select>
              </div>
            </div>
            
            {bulkSelection.length > 0 && (
              <div className="mt-4 sm:mt-0 sm:ml-4 flex space-x-2">
                <button
                  onClick={() => handleBulkAction('activate')}
                  className="btn-secondary text-sm"
                >
                  Activate ({bulkSelection.length})
                </button>
                <button
                  onClick={() => handleBulkAction('deactivate')}
                  className="btn-secondary text-sm"
                >
                  Deactivate ({bulkSelection.length})
                </button>
                <button
                  onClick={() => handleBulkAction('delete')}
                  className="btn-danger text-sm"
                >
                  Delete ({bulkSelection.length})
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Users Table */}
      {filteredUsers.length === 0 ? (
        <div className="text-center py-12">
          <UsersIcon className="mx-auto h-12 w-12 text-primary-400" />
          <h3 className="mt-2 text-sm font-medium text-primary-900">No users found</h3>
          <p className="mt-1 text-sm text-primary-500">
            {searchTerm || roleFilter ? 'No users match your filters.' : 'Get started by creating a new user.'}
          </p>
          {!searchTerm && !roleFilter && (
            <div className="mt-6">
              <button
                type="button"
                onClick={handleCreate}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                New User
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-primary-200">
              <thead className="bg-primary-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={bulkSelection.length === filteredUsers.length}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-primary-300 rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-primary-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-primary-200">
                {filteredUsers.map((userData) => (
                  <tr key={userData._id} className="hover:bg-primary-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={bulkSelection.includes(userData._id)}
                        onChange={() => handleSelectUser(userData._id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-primary-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-600">
                              {userData.firstName?.charAt(0)}{userData.lastName?.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-primary-900">
                            {userData.firstName} {userData.lastName}
                          </div>
                          <div className="text-sm text-primary-500">
                            {userData.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`status-badge ${
                        userData.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {userData.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`status-badge ${userData.isActive ? 'status-active' : 'status-inactive'}`}>
                        {userData.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-500">
                      {userData.lastLogin 
                        ? new Date(userData.lastLogin).toLocaleDateString()
                        : 'Never'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-500">
                      {new Date(userData.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleAssignResources(userData)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Assign Resources"
                      >
                        <Key className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(userData)}
                        className="text-primary-600 hover:text-primary-900 mr-3"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(userData)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      <UserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        user={selectedUser}
      />

      <AssignResourcesModal
        isOpen={!!assignUser}
        onClose={() => setAssignUser(null)}
        user={assignUser}
      />

      <ConfirmDialog
        isOpen={!!deleteUser}
        onClose={() => setDeleteUser(null)}
        onConfirm={confirmDelete}
        title="Delete User"
        message={`Are you sure you want to delete "${deleteUser?.firstName} ${deleteUser?.lastName}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="btn-danger"
      />
    </div>
  )
}

export default Users